import React from 'react';
import { Link } from 'react-router-dom';
import {
  Paper,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Avatar,
  IconButton,
  Skeleton,
  Chip
} from '@mui/material';
import {
  HealthAndSafety as HealthIcon,
  ArrowForward as ArrowIcon,
  PersonOutline as PersonIcon
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';

const safeAge = (ts) => {
  try {
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return formatDistanceToNow(d, { addSuffix: true });
  } catch { return ''; }
};

/**
 * Health Updates Widget
 * Shows recent family health updates
 */
const HealthUpdatesWidget = ({ healthUpdates = [], loading = false }) => {
  if (loading) {
    return (
      <Paper elevation={2} sx={{ p: 3, borderRadius: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
          <Skeleton variant="text" width={150} height={32} />
        </Box>
        {[1, 2, 3].map((i) => (
          <Box key={i} sx={{ mb: 2 }}>
            <Skeleton variant="text" width="80%" height={24} />
            <Skeleton variant="text" width="100%" height={20} />
            <Skeleton variant="text" width="40%" height={16} />
          </Box>
        ))}
      </Paper>
    );
  }

  return (
    <Paper elevation={2} sx={{ p: 3, borderRadius: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar sx={{ bgcolor: '#d32f2f', mr: 2 }}>
            <HealthIcon />
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Health Updates
          </Typography>
        </Box>
        <IconButton
          component={Link}
          to="/health-updates"
          color="primary"
          size="small"
        >
          <ArrowIcon />
        </IconButton>
      </Box>

      {/* Content */}
      {!healthUpdates || healthUpdates.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <HealthIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            No health updates yet
          </Typography>
          <Typography
            variant="caption"
            color="primary"
            component={Link}
            to="/health-updates"
            sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            Add a health update
          </Typography>
        </Box>
      ) : (
        <List sx={{ p: 0 }}>
          {healthUpdates.slice(0, 5).map((update, index) => (
            <ListItem
              key={update.id}
              component={Link}
              to="/health-updates"
              sx={{
                px: 0,
                py: 1.5,
                borderBottom: index < Math.min(healthUpdates.length, 5) - 1 ? '1px solid' : 'none',
                borderColor: 'divider',
                textDecoration: 'none',
                color: 'inherit',
                '&:hover': { bgcolor: 'action.hover' }
              }}
            >
              <ListItemText
                secondaryTypographyProps={{ component: 'div' }}
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1 }}>
                      {update.title}
                    </Typography>
                    {update.memberName && (
                      <Chip
                        icon={<PersonIcon sx={{ fontSize: 16 }} />}
                        label={update.memberName}
                        size="small"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    )}
                  </Box>
                }
                secondary={
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      {update.description?.substring(0, 80)}
                      {update.description?.length > 80 && '...'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      by {update.authorName || update.createdBy} •{' '}
                      {update.createdAt && safeAge(update.createdAt)}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      )}
    </Paper>
  );
};

export default HealthUpdatesWidget;
